import { Injectable } from "@angular/core";
import { collection, Firestore, onSnapshot, query, where } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import { combineLatest, Observable, of } from "rxjs";
import { catchError, filter, map, shareReplay, switchMap, take } from "rxjs/operators";
import { RequestPaymentService } from "src/app/services/RequestBodyService/RequestPayment/request-payment.service";
import { UserManagementService } from "src/app/services/user-management.service";

@Injectable({ providedIn: "root" })
export class WalletService {
  public cards = [];
  isAccountPage: boolean;

paymentDetails$: Observable<any[]> = combineLatest([
  this.authService.getUid$("WalletService"),
  this.userManagementService.userRequest$,
  this.catchService.isOnline$
]).pipe(
  filter(([userId, user, isOnline]) => !!userId && !!user && isOnline),
  // Gate on customTokenReady$ before opening the onSnapshot listener to prevent
  // the permission-denied race condition on iOS/Safari (LOTTOSOCIAL-PWA-1MEE).
  switchMap(([userId]) => {
    const storedCardRef = collection(this.firestore, `/users/${userId}/stored_card_V1`);
    const storedCardQuery = query(storedCardRef, where("status", "==", true));

    return this.authStateService.customTokenReady$.pipe(
      take(1),
      switchMap(() => new Observable<any[]>((observer) => {
        const unsubscribe = onSnapshot(
        storedCardQuery,
        (snapshot: Data) => {
          const cards = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          }));
 
          // Deduplicate by short number
          const uniqArray = cards.filter(
            (card, index, arr) =>
              card &&
              arr.findIndex(
                (t) => t && t.card_short_number === card.card_short_number
              ) === index
          );
 
          // Sort by expiry and primary flag
          const sortedCards = uniqArray
            .sort(
              (a, b) =>
                new Date(
                  parseInt(b.expiry_date?.split("/")[1]),
                  parseInt(b.expiry_date?.split("/")[0]) + 1,
                  0
                ).getTime() -
                new Date(
                  parseInt(a.expiry_date?.split("/")[1]),
                  parseInt(a.expiry_date?.split("/")[0]) + 1,
                  0
                ).getTime()
            )
            .sort((a, b) => b.is_primary - a.is_primary)
            .filter((card) => card.appropriate_flag);
          observer.next(sortedCards);
        },
        (error) => {
          this.authService.errorHandlerLogs(`[PaymentService] ❌ Firestore snapshot error:`, error);
          observer.error(error);
        }
      );
      // Cleanup listener on unsubscribe
      // [PaymentService] 🧹 Unsubscribed Firestore listener for user:
      return () => {
        unsubscribe();
      };
    }))
    );
  }),
  // handle errors
  catchError((err) => {
    this.authService.errorHandlerLogs("[PaymentService] 🔥 Error in paymentDetails$:", err);
    return of([]);
  }),
  shareReplay(1)
);
storedCards$ = this.paymentDetails$.pipe(
    map((paymentDetails) => {
      const storedCards = this.getFilteredWalletCards(paymentDetails);
      return storedCards;
    })
);


  constructor(
    private firestore: Firestore,
    private catchService: CatchErrorService,
    private userManagementService: UserManagementService,
    private RequestPaymentService: RequestPaymentService,
    private authService: AuthService,
    private authStateService: AuthStateService,

  ) { }

  updateCard(id: string, updateInfo: any): void {
    let UserId: string;
    this.authService.getUid$("WalletService").pipe(
      switchMap((userId) => {
        if(!userId) userId = null;
       UserId = userId;
       return of(userId) || of(null);
      }));
    if (!id) return;
    const userId = UserId;
    if (!userId) return;

    const path_id = "5NJtBrNGRpm4rhqR0Izg";
    const doc_data = updateInfo;
    const dv = { docId: id };
    const method = "update";

    this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      dv,
        method,
      }).subscribe({
        error: (err) => {
          console.error('Error updating card:', err);
        }
      });
  }
  removeCard(id: string) {
    if (!id) return;
    return this.removeWalletCard(id);
  }
  removeWalletCard(cardId: string) {
    // const api = this.angularFireFunctions.httpsCallable("api");
    // return api(this.requestBodyService.removeWalletCard(cardId));
    return this.authService?.getV2CloudFunction("apiv2", this.RequestPaymentService.removeWalletCard(cardId));
  }

  getFilteredWalletCards(paymentDetails) {
    if (paymentDetails) {
      const currDate = new Date();
      const cardsPriority = [];
      const cardsWithoutPriority = [];

      // Detect if any card is explicitly ongoing_allowed = false
      const hasOngoingFalse = paymentDetails.some((card) => card.ongoing_allowed === false);

      // Include only if ongoing_allowed is true OR not defined
      const ongoingAllowedCards = paymentDetails.filter(
        (card) => card.ongoing_allowed === true || card.ongoing_allowed === undefined
      );

      // If no ongoing-allowed cards exist
      if (ongoingAllowedCards.length === 0) {
        // Store the flag for later use
        localStorage.setItem("allOngoingFalse", JSON.stringify(hasOngoingFalse));
        // No valid cards — return empty list
        this.cards = [];
        return this.cards;
      }

      // Reset the flag if we do have ongoing allowed cards
      localStorage.setItem("allOngoingFalse", JSON.stringify(false));

      // Separate cards with and without priority
      const withPriority = ongoingAllowedCards.filter((card) => card.hasOwnProperty("priority"));
      const withoutPriority = ongoingAllowedCards.filter((card) => !card.hasOwnProperty("priority"));

      // Handle priority cards
      for (const card of withPriority) {
        if (card?.expiry_date) {
          card.isChecked = false;
          const [month, year] = card.expiry_date.split("/");
          const cardDate = new Date(year, month, 1);
          if (card?.status && cardDate.getTime() > currDate.getTime()) {
            cardsPriority.push(card);
          }
        }
      }

      // Handle non-priority cards
      for (const card of withoutPriority) {
        if (card?.expiry_date) {
          card.isChecked = false;
          const [month, year] = card.expiry_date.split("/");
          const cardDate = new Date(year, month, 1);
          if (card?.status && cardDate.getTime() > currDate.getTime()) {
            if (card?.is_primary) {
              cardsWithoutPriority.unshift(card);
            } else {
              cardsWithoutPriority.push(card);
            }
          }
        }
      }

      // Sort and finalize card list
      cardsPriority.sort((a, b) => b?.priority - a?.priority);
      this.cards = [...cardsPriority, ...cardsWithoutPriority];
      if (this.cards[0]) this.cards[0].isChecked = true;

      return this.cards;
    } else {
      // Reset localStorage flag when no payment details exist
      localStorage.setItem("allOngoingFalse", JSON.stringify(false));
      return [];
    }
  }
}
